import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type ConnectionStatus = 'none' | 'pending' | 'pending_sent' | 'pending_received' | 'connected';

export function useConnectionStatus(targetUserId: string) {
  const { user } = useAuth();
  const [status, setStatus] = useState<ConnectionStatus>('none');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!user || !targetUserId || user.id === targetUserId) return;
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.rpc('get_connection_status', { target_user: targetUserId });
    if (error) {
      console.error('Error fetching connection status:', error);
      setError('Error fetching connection status');
    } else {
      // Map database status to frontend status
      const dbStatus = data as string;
      let mappedStatus: ConnectionStatus = 'none';
      
      if (dbStatus === 'connected') {
        mappedStatus = 'connected';
      } else if (dbStatus === 'pending_sent') {
        mappedStatus = 'pending_sent';
      } else if (dbStatus === 'pending_received') {
        mappedStatus = 'pending_received';
      } else if (dbStatus === 'pending') {
        // Fallback for old format
        mappedStatus = 'pending';
      }
      
      setStatus(mappedStatus);
    }
    setLoading(false);
  }, [user, targetUserId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const sendRequest = useCallback(async () => {
    if (!user || !targetUserId) return;
    setLoading(true);
    setError(null);
    const { error } = await supabase.rpc('send_connection_request', { target_user: targetUserId });
    if (error) {
      if (error.code === '409') {
        setError('Request already sent or connection already exists.');
        await fetchStatus();
      } else {
        setError('Error sending connection request');
      }
    } else {
      await fetchStatus();
    }
    setLoading(false);
  }, [user, targetUserId, fetchStatus]);

  return { status, loading, error, sendRequest, refetch: fetchStatus };
} 